/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type DeleteNodeGroupVariables = {
  nodeGroupId: string;
};

export type DeleteNodeGroupMutationConfig = {
  onSuccess?: (variables: DeleteNodeGroupVariables) => void;
  onError?: (error: unknown, variables: DeleteNodeGroupVariables) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for deleting a node group.
 *
 * @param config.onSuccess - Called after node group is successfully deleted
 * @param config.onError - Called if deletion fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for deleting a node group
 */
export function useDeleteNodeGroupMutation(config: DeleteNodeGroupMutationConfig) {
  const queryClient = useQueryClient();
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, mutateAsync, isError, error, isPending } = useMutation<void, ApiError, DeleteNodeGroupVariables>({
    mutationFn: ({ nodeGroupId }: DeleteNodeGroupVariables) => {
      return mgmtPlaneAPIClient.nodeGroups.deleteNodeGroup({
        tenantId: tenantId!,
        nodeGroupId,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nodeGroups', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      config?.onSuccess?.(variables);
    },
    onError: config?.onError,
    onSettled: config?.onSettled,
  });

  return { mutate, mutateAsync, isError, error, isPending };
}
